<script lang="ts">
    import { CheckCircleIcon, CheckIcon } from "svelte-feather-icons";
    import {
        Answer,
        AnswerAlternatives,
        AnsweredQuestions,
        AnswerID,
        AnswerNumber,
        AutocompleteOption,
        getDefaultAnswer,
        getPreferredResortComparisonQuestions,
        getPreferredResortRankingTopics,
        isResortComparisonQuestion,
        Question,
        QuestionID,
    } from "../questions";
    import { QuestionType } from "../questions";
    import AutoComplete from "simple-svelte-autocomplete";
    import type { SearchResult } from "../search";
    import BarGraphSlider from "./BarGraphSlider.svelte";
    import { formatDuration, formatDurationTooltip } from "../formatting";
    import type { Histogram } from "../bar_graph";
    import { fly, TransitionConfig } from "svelte/transition";
    import { getPreviouslyVisitedResorts } from "../utils";

    const durationSplits = [0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20].map((x) => x * 3600);

    const histogram = (xs: number[], bucketSplits: number[] | null) => {
        xs = xs.filter((x) => x !== null);
        const mx = Math.max(...xs);
        const mn = Math.min(...xs);
        if (bucketSplits === null) {
            const buckets = 10;
            bucketSplits = new Array(buckets + 1).fill(0);
            for (let i = 0; i <= buckets; i++) {
                bucketSplits[i] = mn + (mx - mn) * (i / buckets);
            }
        }
        const bucketSizes = new Array(bucketSplits.length - 1).fill(0);
        for (let i = 0; i < xs.length; i++) {
            let bucketIndex = bucketSplits.findIndex((s) => s >= xs[i]) - 1;
            if (bucketIndex == -2) bucketIndex = bucketSplits.length - 2;
            bucketIndex = Math.max(0, bucketIndex);
            bucketSizes[bucketIndex] += 1;
        }
        return {
            bucketSplits,
            bucketSizes,
        };
    };

    export let resorts: SearchResult[];
    export let question: Question;
    export let answer: Answer = getDefaultAnswer(question);
    export let answered: AnsweredQuestions;
    export let onChange: ((newAnswer: Answer) => void) | null;
    export let inTransition: (node: Element, config: {}) => TransitionConfig = (a, b) => {
        return { duration: 0 };
    };
    export let outTransition: (node: Element, config: {}) => TransitionConfig = (a, b) => {
        return { duration: 0 };
    };

    let header = "";
    let alternatives: { id: number; label: string }[] | null = null;
    let hist: Histogram = histogram(
        resorts.map((x) => x.travel_time_in_seconds),
        durationSplits
    );

    const toggleSelected = (id: number) => {
        if (!(answer instanceof AnswerAlternatives)) {
            return;
        }
        const selected = new Set(answer.selected);
        if (selected.has(id)) {
            selected.delete(id);
        } else {
            if (id == AnswerID.NoneOfThese) {
                selected.clear();
            } else {
                selected.delete(AnswerID.NoneOfThese);
            }
            selected.add(id);
        }
        answer.selected = selected;
        if (onChange != null) onChange(answer);
    };

    const setSelected = (id: number) => {
        if (!(answer instanceof AnswerAlternatives)) {
            return;
        }
        const selected = new Set<number>();
        selected.add(id);
        answer.selected = selected;
        if (onChange != null) onChange(answer);
    };

    const updateAnswerForAutocomplete = () => {
        const newAnswer = new AnswerNumber(selectedAutocompleteOption ? selectedAutocompleteOption.id : null);
        if (newAnswer.selected != answer.selected && onChange != null) {
            answer.selected = newAnswer.selected;
            onChange(answer);
        }
    };

    let autocompleteText = "";
    let autocompleteOptions: AutocompleteOption[] = [];
    let selectedAutocompleteOption: AutocompleteOption | null = null;
    if (question.type === QuestionType.Autocomplete) {
        autocompleteOptions = question.alternatives;
        if (answer instanceof AnswerNumber) {
            selectedAutocompleteOption = autocompleteOptions.find((option) => option.id == answer.selected) || null;
        }
    }

    const integerRangeHist = (min: number, max: number): Histogram => {
        let splits = [];
        for (let i = min; i <= max; i++) {
            splits.push(i);
        }
        return {
            bucketSplits: splits,
            bucketSizes: new Array(splits.length - 1).fill(0),
        };
    };

    $: {
        hist = histogram(
            resorts.map((x) => x.travel_time_in_seconds),
            durationSplits
        );
    }

    const fly_anim = (node: Element, a: {}) => fly(node, { x: -200, duration: 400 });
    $: {
        if (
            question.id == QuestionID.PreviouslyVisitedResorts &&
            question.type === QuestionType.Alternatives &&
            !question.alternatives
        ) {
            const resortCount = resorts.length;
            if (resortCount >= 5) {
                const weights: number[] = [];
                let totalWeight = 0;
                for (let i = 0; i < resortCount; i += 1) {
                    const resort = resorts[i];
                    const weight = (resort.num_lifts + 1) / (i + 5);
                    weights.push(weight);
                    totalWeight += weight;
                }
                const pickedResorts: SearchResult[] = [];
                while (pickedResorts.length < 5) {
                    let r = Math.random() * totalWeight;
                    for (let i = 0; i < resortCount; i += 1) {
                        if (r < weights[i]) {
                            const pick = resorts[i];
                            if (!pickedResorts.includes(pick)) {
                                pickedResorts.push(pick);
                            }
                            break;
                        }
                        r -= weights[i];
                    }
                }
                const alternatives = pickedResorts.map((resort) => {
                    return { id: resort.integer_id, label: resort.name };
                });
                alternatives.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
                alternatives.push({ id: AnswerID.NoneOfThese, label: "Ingen av dessa" });
                question.alternatives = alternatives;
            }
        }
    }

    $: {
        if (isResortComparisonQuestion(question.id) && question.type === QuestionType.Alternatives) {
            const pickedResorts = getPreviouslyVisitedResorts(answered, resorts);
            const alternatives = pickedResorts.map((resort) => {
                return { id: resort.integer_id, label: resort.name };
            });
            alternatives.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
            alternatives.push({ id: AnswerID.NoneOfThese, label: "Ingen av dessa" });
            question.alternatives = alternatives;
        }
    }

    $: {
        let newHeader = question.header;
        if (question.id == QuestionID.SingleResortRating) {
            const pickedResorts = getPreviouslyVisitedResorts(answered, resorts);
            if (pickedResorts.length == 1) {
                const pickedResort = pickedResorts[0];
                newHeader = question.header.replace("{resort_name}", pickedResort.name);
            }
        }
        header = newHeader;
    }

    $: {
        if (question.type === QuestionType.Alternatives) {
            let newAlternatives = question.alternatives;
            if (question.id == QuestionID.SingleResortRating) {
                const preferredTopics = getPreferredResortRankingTopics(answered);
                newAlternatives = newAlternatives.filter((a) => preferredTopics.includes(a.id));
            }
            alternatives = newAlternatives;
        }
    }
</script>

<div class="question" in:inTransition out:outTransition>
    <h2>{header}</h2>
    {#if question.type === QuestionType.Alternatives && answer instanceof AnswerAlternatives}
        <div class="question-alternatives{alternatives && alternatives.length > 7 ? ' wrapped' : ''}">
            {#if alternatives}
                {#each alternatives as alt (alt.id)}
                    {#if question.count.max == 1}
                        <div
                            class="question-alternative"
                            class:selected={answer.selected.has(alt.id)}
                            on:click={() => setSelected(alt.id)}
                        >
                            <span class="radiobutton">
                                <CheckIcon />
                            </span>
                            <span class="text">{alt.label}</span>
                        </div>
                    {:else}
                        <div
                            class="question-alternative"
                            class:selected={answer.selected.has(alt.id)}
                            on:click={() => toggleSelected(alt.id)}
                        >
                            <span class="checkbox">
                                <CheckIcon />
                            </span>
                            <span class="text">{alt.label}</span>
                        </div>
                    {/if}
                {/each}
            {/if}
        </div>
    {:else if question.type === QuestionType.Autocomplete && answer instanceof AnswerNumber}
        <AutoComplete
            items={autocompleteOptions}
            bind:selectedItem={selectedAutocompleteOption}
            bind:text={autocompleteText}
            labelFieldName="label"
            onChange={() => updateAnswerForAutocomplete()}
            noResultsText="Inga resultat"
            hideArrow="true"
        />
    {:else if question.type === QuestionType.TravelTime && answer instanceof AnswerNumber}
        <BarGraphSlider
            histogram={hist}
            format={formatDuration}
            formatTooltip={formatDurationTooltip}
            value={answer.selected || hist.bucketSplits[hist.bucketSplits.length - 1]}
            selectableIndexRange={[1, null]}
            onChange={(v) => {
                if (hist === null) throw "histogram is null";
                answer.selected = v;
                if (onChange != null) onChange(answer);
            }}
        />
    {:else if question.type === QuestionType.IntegerSlider && answer instanceof AnswerNumber}
        <BarGraphSlider
            histogram={integerRangeHist(question.min, question.max)}
            format={(x) => (x != 1 ? `${x} dagar` : "1 dag")}
            value={answer.selected || 4}
            selectableIndexRange={[null, null]}
            showBars={false}
            onChange={(v) => {
                if (hist === null) throw "histogram is null";
                answer.selected = v;
                if (onChange != null) onChange(answer);
            }}
        />
    {/if}
</div>
